import { Box, CircularProgress, Typography } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import {
  useCreateProjectDocumentRule,
  useDeleteProjectDocumentRule,
  useGetProjectDocumentRules,
  useUpdateProjectDocumentRules,
} from '@/service-library/hooks/project-document-rules'
import { ProjectDocumentRule } from '@/types/rules'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import { DraggableList } from '@/components/lists/DraggableList'
import RuleCard from '@/components/rules/RuleCard'
import AddDocumentRuleDialog from './AddDocumentRuleDialog'

type DocumentRulesProps = {
  documentRulesQuery: ReturnType<typeof useGetProjectDocumentRules>
  selectedRuleId: string | undefined
  setSelectedRuleId: (ruleId: string | undefined) => void
  /** Opened by the "Add rule" button in the page heading. */
  addRuleOverlay: OverlayState
}

export default function DocumentRules({
  documentRulesQuery,
  selectedRuleId,
  setSelectedRuleId,
  addRuleOverlay,
}: DocumentRulesProps) {
  const { projectDocumentRules = [], isPending, queryKey } = documentRulesQuery

  const { createProjectDocumentRule } = useCreateProjectDocumentRule({
    listQueryKey: queryKey,
    onMutate: (newDocumentRule) => {
      setSelectedRuleId(newDocumentRule.id)
    },
    onSuccess: () => {
      showSuccessSnackbar('Document Rule Created')
    },
    onError: () => {
      showErrorSnackbar(
        'Failed to create document rule. Please try again later.',
      )
      setSelectedRuleId(undefined)
    },
  })

  const { deleteProjectDocumentRule } = useDeleteProjectDocumentRule({
    listQueryKey: queryKey,
    onMutate: () => {
      setSelectedRuleId(undefined)
    },
    onError: () => {
      showErrorSnackbar('Failed to delete field rule. Please try again later.')
    },
  })

  const { updateProjectDocumentRules } = useUpdateProjectDocumentRules({
    listQueryKey: queryKey,
    useSortOrder: true,
    onMutate: (updatedDocRules) => {
      if (selectedRuleId) {
        const updatedSelectedRule = updatedDocRules.find(
          ({ id }) => selectedRuleId === id,
        )
        if (updatedSelectedRule) {
          setSelectedRuleId(updatedSelectedRule.id)
        }
      }
    },
  })

  const reorderDocumentRules = (
    reorderedDocumentRules: ProjectDocumentRule[],
  ) => {
    const sortedDocumentRules = reorderedDocumentRules.map((item, index) => ({
      ...item,
      sort_order: index,
    }))
    updateProjectDocumentRules(sortedDocumentRules)
  }

  return (
    <>
      {isPending && (
        <Box sx={{ p: 2 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {!isPending && projectDocumentRules.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          No document rules yet. Add a rule to start checking documents.
        </Typography>
      )}

      {!isPending && projectDocumentRules.length > 0 && (
        <DraggableList
          data={projectDocumentRules}
          onChange={reorderDocumentRules}
        >
          {projectDocumentRules.map((documentRule) => (
            <RuleCard
              key={documentRule.id}
              isSelected={selectedRuleId === documentRule.id}
              onClick={() => {
                setSelectedRuleId(documentRule.id)
              }}
              onDelete={() => {
                deleteProjectDocumentRule(documentRule.id)
              }}
              name={documentRule.type?.name || ''}
              description={documentRule.type?.description || ''}
              funcName={documentRule.params.function_name}
              enabled={documentRule.is_enabled}
              onEnableDisable={(checked) => {
                updateProjectDocumentRules([
                  { ...documentRule, is_enabled: checked },
                ])
              }}
            />
          ))}
        </DraggableList>
      )}

      <AddDocumentRuleDialog
        overlay={addRuleOverlay}
        onSubmit={createProjectDocumentRule}
      />
    </>
  )
}
